import type { ManualChapter } from './types'

export const gamificationChapter: ManualChapter = {
  id: 'gamification',
  number: 10,
  title: 'Gamification',
  description: 'Badge, punti, escape room e tornei',
  icon: 'TrophyIcon',
  sections: [
    {
      id: 'overview',
      title: 'Sistema di Gamification',
      requirement: '§2.6 Sistema di gamification',
      content: `
## Sistema di Gamification

La gamification rende l'apprendimento piu coinvolgente attraverso elementi di gioco.

### Elementi del Sistema

| Elemento | Descrizione |
|----------|-------------|
| **Punti** | Accumula punti con le attivita |
| **Badge** | Ottieni riconoscimenti speciali |
| **Livelli** | Avanza nel tuo percorso |
| **Classifiche** | Confrontati con gli altri |
| **Escape Room** | Sfide di gruppo immersive |
| **Tornei** | Competizioni a tempo |

### Filosofia

Il sistema premia:
- **Partecipazione attiva** alla community
- **Completamento** dei percorsi formativi
- **Qualita** dei contributi
- **Collaborazione** con gli altri membri
`
    },
    {
      id: 'points',
      title: 'Sistema Punti',
      requirement: '§2.6 Classifiche e punti',
      content: `
## Sistema Punti

### Come Guadagnare Punti

| Attivita | Punti |
|----------|-------|
| Completare una lezione | 10 |
| Superare un quiz | 20-50 |
| Completare un corso | 100 |
| Pubblicare nel forum | 5 |
| Ricevere "Utile" su un post | 10 |
| Risposta marcata come soluzione | 25 |
| Partecipare a un evento | 15 |
| Completare task nella Task Force | 20 |
| Vincere un torneo | 100+ |
| Completare Escape Room | 50+ |

### Dove Vedere i Punti

I tuoi punti sono visibili:
- Nel **profilo** personale
- Nella **dashboard**
- Nelle **classifiche**

### A Cosa Servono

I punti determinano:
- La tua posizione in **classifica**
- Il tuo **livello** nella community
- L'accesso a **contenuti esclusivi** (se configurato)
`
    },
    {
      id: 'badges',
      title: 'Badge e Riconoscimenti',
      requirement: '§2.6 Badge personalizzabili',
      content: `
## Badge e Riconoscimenti

I badge sono riconoscimenti virtuali per i tuoi traguardi.

### Tipi di Badge

| Categoria | Esempi |
|-----------|--------|
| **Formazione** | Primo corso, 5 corsi, Master |
| **Forum** | Prima risposta, Esperto, Top contributor |
| **Eventi** | Partecipante, Speaker, Organizzatore |
| **Task Force** | Membro attivo, Leader, Completamento |
| **Speciali** | Early adopter, Beta tester, Anniversario |

### Come Ottenere Badge

I badge si ottengono automaticamente quando:
- Raggiungi determinati **traguardi**
- Completi **sfide** specifiche
- Partecipi a **eventi** speciali

### Visualizzare i Badge

1. Vai sul tuo **Profilo**
2. Scorri alla sezione **Badge**
3. Vedi i badge ottenuti e quelli ancora da sbloccare

### Badge Bloccati

I badge non ancora ottenuti mostrano:
- **Sagoma grigia** del badge
- **Requisiti** per ottenerlo
- **Progresso** attuale (se applicabile)
`
    },
    {
      id: 'leaderboard',
      title: 'Classifiche',
      content: `
## Classifiche

Le classifiche mostrano i membri piu attivi della community.

### Tipi di Classifica

| Classifica | Criteri |
|------------|---------|
| **Generale** | Punti totali accumulati |
| **Mensile** | Punti del mese corrente |
| **Per categoria** | Punti per area specifica |
| **Per corso** | Migliori in un corso |

### Come Funziona

- Le classifiche si aggiornano in **tempo reale**
- La tua posizione dipende dai **punti** accumulati
- Puoi vedere la tua posizione e quelle vicine

### Visualizzare le Classifiche

1. Vai alla sezione **Gamification** o **Community**
2. Clicca su **Classifiche**
3. Scegli il tipo di classifica
4. Scorri per vedere le posizioni

### Privacy

- Puoi scegliere se apparire nelle classifiche
- Vai su **Impostazioni** → **Privacy**
- Attiva/disattiva **"Mostra in classifica"**
`
    },
    {
      id: 'escape-room',
      title: 'Escape Room',
      requirement: '§2.6 Escape room tematiche (multiplayer a tempo)',
      content: `
## Escape Room

Le Escape Room sono **sfide di gruppo** a tempo con enigmi tematici.

### Come Funziona

1. **Team**: Forma un gruppo (2-6 persone)
2. **Stanza**: Entra in una stanza virtuale
3. **Enigmi**: Risolvi puzzle e quesiti
4. **Tempo**: Completa prima che scada
5. **Punteggio**: Guadagna punti in base al tempo

### Partecipare a un'Escape Room

1. Vai alla sezione **Escape Room**
2. Scegli una stanza disponibile
3. **Crea** un team o **unisciti** a uno esistente
4. Attendi che il team sia completo
5. **Inizia** quando tutti sono pronti

### Durante la Sfida

| Elemento | Descrizione |
|----------|-------------|
| **Timer** | Tempo rimanente |
| **Enigmi** | Puzzle da risolvere |
| **Chat** | Comunicazione con il team |
| **Hint** | Suggerimenti (riducono il punteggio) |
| **Progressi** | Enigmi risolti su totale |

### Tipi di Enigmi

- **Quiz** a risposta multipla
- **Puzzle** logici
- **Ricerca** di informazioni
- **Decodifica** di messaggi
- **Abbinamenti** corretti

### Punteggio

Il punteggio dipende da:
- **Tempo** impiegato (meno = meglio)
- **Hint** utilizzati (meno = meglio)
- **Errori** commessi (meno = meglio)
- **Bonus** per soluzioni particolari
`
    },
    {
      id: 'tournaments',
      title: 'Tornei',
      requirement: '§2.6 Tornei competitivi',
      content: `
## Tornei

I tornei sono **competizioni** su argomenti specifici.

### Tipi di Torneo

| Tipo | Descrizione |
|------|-------------|
| **Quiz Race** | Chi risponde piu velocemente |
| **Maratona** | Piu domande possibili in un tempo |
| **Eliminazione** | Chi sbaglia viene eliminato |
| **A punti** | Accumula punti in piu round |

### Partecipare a un Torneo

1. Vai alla sezione **Tornei**
2. Vedi i tornei **disponibili** e **in programma**
3. Clicca su **"Iscriviti"**
4. Partecipa alla data e ora indicata

### Durante il Torneo

- **Lobby**: Attendi l'inizio
- **Round**: Rispondi alle domande
- **Classifica live**: Vedi la tua posizione
- **Finale**: I migliori si sfidano

### Premi

I vincitori ottengono:
- **Punti bonus** significativi
- **Badge** esclusivi
- **Riconoscimento** in classifica
- Eventuali **premi fisici** (se previsti)

### Calendario Tornei

- I tornei hanno **date e orari** fissi
- Ricevi **notifica** prima dell'inizio
- Puoi vedere il **calendario** tornei
`
    },
    {
      id: 'my-achievements',
      title: 'I Miei Traguardi',
      content: `
## I Miei Traguardi

### Dashboard Gamification

Nel tuo profilo trovi un riepilogo di:

| Elemento | Descrizione |
|----------|-------------|
| **Punti totali** | Accumulati da sempre |
| **Livello** | Il tuo grado attuale |
| **Badge** | Quanti ne hai ottenuti |
| **Posizione** | In classifica generale |

### Storico Attivita

Puoi vedere:
- **Punti guadagnati** per attivita
- **Badge ottenuti** con data
- **Tornei** a cui hai partecipato
- **Escape Room** completate

### Progressi

Per ogni obiettivo vedi:
- **Stato attuale** vs obiettivo
- **Percentuale** di completamento
- **Cosa manca** per raggiungerlo

### Condivisione

Puoi condividere i tuoi traguardi:
- Sul tuo **profilo** pubblico
- Con un **link** diretto
- Con **screenshot** (prossimamente social)
`
    }
  ]
}
